import React from 'react';
import { ReactComponent as HomeSmile } from '../../../assets/icons/home-smile.svg';
import { ReactComponent as ChevronRight } from '../../../assets/icons/chevron-right.svg';
import AssetImage from '../../AssetImage';
import { ImageUploadTrigger } from '../../imageUploader/ImageUploadFlow';
import { AssetProposalAsset } from '../hooks/assetProposalUtils';
import { AssetEdit } from '../hooks/useAssetProposalState';

interface AssetEditRowProps {
  asset: AssetProposalAsset;
  edit: AssetEdit;
  isExpanded: boolean;
  isSettled: boolean;
  onToggle: () => void;
  onEditName: (name: string) => void;
  onEditDescription: (description: string) => void;
  onOpenImageUpload: () => void;
}

const AssetEditRow: React.FC<AssetEditRowProps> = ({
  asset,
  edit,
  isExpanded,
  isSettled,
  onToggle,
  onEditName,
  onEditDescription,
  onOpenImageUpload,
}) => (
  <div>
    <button
      type="button"
      className={`w-full flex items-center gap-3 py-2 text-left rounded-md transition-colors ${!isSettled ? 'hover:bg-eai-surface-sunken' : 'cursor-default'}`}
      onClick={() => !isSettled && onToggle()}
      disabled={isSettled}
    >
      <AssetImage
        asset={{ ...asset, imageUrl: edit.imageUrl }}
        assetName={edit.name}
        size={32}
        className="flex-shrink-0 !rounded-md"
        fallbackIcon={<HomeSmile className="h-4 w-4" />}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <p className="text-sm font-medium text-eai-ink truncate">{edit.name}</p>
          {!isSettled && (
            <ChevronRight className={`w-3 h-3 flex-shrink-0 text-eai-ink-faint transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          )}
        </div>
        <p className="text-xs text-eai-ink-faint">{asset.type}</p>
      </div>
    </button>

    {isExpanded && (
      <div className="pb-3 space-y-4">
        <div>
          <label className="block text-xs text-eai-ink-subtle font-medium mb-1">Name</label>
          <input
            type="text"
            value={edit.name}
            onChange={(e) => onEditName(e.target.value)}
            className="eai-input"
          />
        </div>
        <div>
          <ImageUploadTrigger
            label="Asset Image"
            onClick={onOpenImageUpload}
            previewNode={
              <AssetImage
                asset={{ imageUrl: edit.imageUrl }}
                assetName={edit.name}
                size={48}
                className="!rounded-md"
                fallbackIcon={<HomeSmile className="h-5 w-5 text-eai-ink-faint" />}
              />
            }
          />
        </div>
        <div>
          <label className="block text-xs text-eai-ink-subtle font-medium mb-1">Description</label>
          <textarea
            value={edit.description}
            onChange={(e) => onEditDescription(e.target.value)}
            rows={2}
            className="w-full border border-eai-border-hover rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-eai-focus resize-none"
            placeholder="Add a description..."
          />
        </div>
      </div>
    )}
  </div>
);

export default AssetEditRow;
